/**
 * Google OAuth for login/register — client ID loaded from API at runtime.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { API_URL } from '../config';

const BUILD_TIME_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || '';

function loadScript(src, id) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function GoogleSignInButtonInner({ clientId, onSuccess, disabled }) {
  const buttonRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  // Keep the latest onSuccess in a ref so the GIS callback identity never
  // changes — otherwise every parent re-render (e.g. a keystroke in the
  // form) would tear down and rebuild the button/iframe mid-flight.
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const handleCredential = useCallback(async (response) => {
    if (!response?.credential) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/oauth/google`, {
        id_token: response.credential,
      });
      onSuccessRef.current?.(res.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  }, []);

  const renderGoogleButton = useCallback(() => {
    if (!buttonRef.current || !window.google?.accounts?.id || !clientId) return;
    buttonRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(buttonRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      width: 320,
      text: 'continue_with',
      shape: 'pill',
    });
    setReady(true);
  }, [clientId]);

  useEffect(() => {
    if (!clientId || disabled) return undefined;

    let cancelled = false;
    let rafId = null;

    (async () => {
      try {
        await loadScript('https://accounts.google.com/gsi/client', 'google-gsi');
        if (cancelled || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredential,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        renderGoogleButton();
        rafId = requestAnimationFrame(() => {
          if (!cancelled) renderGoogleButton();
        });
      } catch {
        if (!cancelled) toast.error('Could not load Google sign-in');
      }
    })();

    return () => {
      cancelled = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // handleCredential and renderGoogleButton are now stable across parent
    // re-renders (see onSuccessRef above), so this effect only actually
    // re-runs when clientId/disabled genuinely change — not on every
    // keystroke in the surrounding form.
  }, [clientId, disabled, handleCredential, renderGoogleButton]);

  return (
    <div className="w-full" data-testid="google-signin">
      {loading && (
        <button type="button" disabled className="w-full py-3 border border-border rounded-xl flex items-center justify-center gap-2 opacity-70">
          <Loader2 className="w-4 h-4 animate-spin" /> Signing in with Google…
        </button>
      )}
      <div
        ref={buttonRef}
        className={loading ? 'hidden' : 'flex justify-center w-full min-h-[44px]'}
        style={{ minWidth: 320 }}
      />
      {!ready && !loading && (
        <p className="text-xs text-center text-muted-foreground mt-1">Loading Google sign-in…</p>
      )}
    </div>
  );
}

export function GoogleOAuthSection({ onSuccess, disabled }) {
  const [clientId, setClientId] = useState(BUILD_TIME_CLIENT_ID);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/auth/oauth/status`);
        if (!cancelled && res.data?.google && res.data?.google_client_id) {
          setClientId(res.data.google_client_id);
        } else if (!cancelled && !BUILD_TIME_CLIENT_ID) {
          setClientId('');
        }
      } catch {
        // Keep build-time client ID if API unreachable
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!checked || !clientId) return null;

  return (
    <>
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-sm text-muted-foreground">or continue with</span>
        <div className="flex-1 h-px bg-border" />
      </div>
      <GoogleSignInButtonInner clientId={clientId} onSuccess={onSuccess} disabled={disabled} />
    </>
  );
}

/** @deprecated Use GoogleOAuthSection */
export function GoogleSignInButton(props) {
  return <GoogleOAuthSection {...props} />;
}

/** @deprecated Included in GoogleOAuthSection */
export function OAuthDivider() {
  return null;
}

export function isOAuthEnabled() {
  return Boolean(BUILD_TIME_CLIENT_ID);
}
